import { HSOverlay } from "preline";
import { WorkspaceDropdown } from "./WorkspaceDropdown";
import { SidebarUpgradeBox } from "./SidebarUpgradeBox";

export function Sidebar({ createWSModalOpen, upgradeModalOpen }: { createWSModalOpen: () => void; upgradeModalOpen: () => void; }) {
  const children = {
    workspaceDropdown: WorkspaceDropdown({ createWSModalOpen, upgradeModalOpen, closeSidebar: close }),
    sidebarUpgradeBox: SidebarUpgradeBox()
  };

  let $sidebarElem: null | HTMLElement = null;

  function closeSidebarOnNavClick() {
    const sidebarNavItems = $sidebarElem!.querySelectorAll(".sidebar-nav-list > li > a");
    sidebarNavItems.forEach((item) => {
      item.addEventListener("click", () => {
        close();
      });
    });
  }

  function updateSidebarActiveItem(path: string | undefined = undefined) {
    const currentPath = path || window.location.pathname;
    const sidebarItems = document.querySelectorAll(".sidebar-nav-list > li > a");

    sidebarItems?.forEach((item) => {
      const link = item as HTMLAnchorElement;
      const linkHref = link.getAttribute("href");

      // Check if the href matches the beginning of the current path (for inner routes)
      if (linkHref && currentPath.startsWith(linkHref)) {
        link.classList.add("bg-gray-100", "dark:bg-neutral-700");
      } else {
        link.blur();
        link.classList.remove("bg-gray-100", "dark:bg-neutral-700");
      }
    });
  }

  function close() {
    setTimeout(() => {
      HSOverlay.close($sidebarElem!);
    }, 200);
  }

  function init() {
    $sidebarElem = document.querySelector("#hs-pro-sidebar") as HTMLElement;

    if (!$sidebarElem) {
      console.error("Sidebar elements not found!");
    }

    closeSidebarOnNavClick();
  }

  function render() {
    return /*html*/`
    <!-- ========== MAIN SIDEBAR ========== -->
    <aside id="hs-pro-sidebar"
    class="hs-overlay [--auto-close:lg] hs-overlay-open:translate-x-0 -translate-x-full transition-all duration-300 transform w-[260px] h-full hidden fixed inset-y-0 start-0 z-[60] bg-white border-e border-gray-200 lg:block lg:translate-x-0 lg:end-auto lg:bottom-0 dark:bg-neutral-800 dark:border-neutral-700"
    tabindex="-1" aria-label="Sidebar">
    <div class="relative flex flex-col h-full max-h-full pt-3">
      <header class="h-[46px] px-8">
        <!-- Logo -->
        <a class="flex-none rounded-md text-xl inline-block font-semibold focus:outline-none focus:opacity-80 cursor-pointer"
          href="https://www.feedspace.io/" target="_blank" aria-label="Feedspace" title="Feedspace">
          <img src="/images/feedspace-logo-light-mode.svg" alt="Feedspace Logo" title="Feedspace" class="w-32 h-auto dark:hidden cursor-pointer" />
          <img src="/images/dark-feedspace-logo.svg" alt="Feedspace Logo" title="Feedspace" class="w-32 h-auto hidden dark:block cursor-pointer" />
        </a>
        <!-- End Logo -->
      </header>

      <!-- Content -->
      <div
        class="flex flex-col mt-1.5 h-full overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-track]:bg-neutral-700 dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500">
        <!-- Nav -->
        <nav class="hs-accordion-group pb-3 w-full flex flex-col flex-wrap" data-hs-accordion-always-open>
          <ul class="flex flex-col gap-y-1 sidebar-nav-list">
            <!-- Link -->
            <li class="px-5">
              <a class="flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700"
                href="/feedbox" data-navigo>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="lucide lucide-gallery-vertical shrink-0 mt-0.5 size-4">
                  <path d="M3 2h18" />
                  <rect width="18" height="12" x="3" y="6" rx="2" />
                  <path d="M3 22h18" />
                </svg>
                Feedbox
              </a>
            </li>
            <!-- End Link -->

            <!-- Link -->
            <li class="px-5">
              <a class="flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700"
                href="/forms" data-navigo>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="lucide lucide-clipboard-list shrink-0 mt-0.5 size-4">
                  <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <path d="M12 11h4" />
                  <path d="M12 16h4" />
                  <path d="M8 11h.01" />
                  <path d="M8 16h.01" />
                </svg>
                Forms
              </a>
            </li>
            <!-- End Link -->

            <!-- Link -->
            <li class="px-5">
              <a href="#imports?tab=google-reviews" aria-haspopup="dialog" aria-expanded="false" aria-controls="hs-full-screen-modal" class="cursor-pointer flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="lucide lucide-download shrink-0 mt-0.5 size-4">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" x2="12" y1="15" y2="3" />
                </svg>
                Import
              </a>
            </li>
            <!-- End Link -->

            <!-- Link -->
            <li class="px-5">
              <a class="flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700"
                href="/pages" data-navigo>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="lucide lucide-app-window-mac shrink-0 mt-0.5 size-4">
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="M6 8h.01" />
                  <path d="M10 8h.01" />
                  <path d="M14 8h.01" />
                </svg>
                Pages
              </a>
            </li>
            <!-- End Link -->

            <!-- Link -->
            <li class="px-5">
              <a class="h-9 flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700 items-center gap-1" href="/widgets" data-navigo>
                <div class="flex items-center gap-x-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-layout-template shrink-0 mt-0.5 size-4">
                    <rect width="18" height="7" x="3" y="3" rx="1"></rect>
                    <rect width="9" height="7" x="3" y="14" rx="1"></rect>
                    <rect width="5" height="7" x="16" y="14" rx="1"></rect>
                  </svg>
                  Widgets
                </div>
                <span class="ms-auto inline-flex items-center gap-2 py-px px-1.5 rounded text-[10px] leading-4 font-medium bg-blue-100 text-blue-800 dark:bg-neutral-700 dark:text-neutral-300">
                  Upcoming
                </span>
              </a>
            </li>
            <!-- End Link -->

            <!-- Link -->
            <!-- <li class="px-5">
              <a
                class="h-9 flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700 items-center gap-1"
                href="/ai-studio"
                data-navigo>
                <div class="flex items-center gap-x-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-bot-message-square shrink-0 mt-0.5 size-4">
                    <path d="M12 6V2H8" />
                    <path
                      d="m8 18-4 4V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2Z" />
                    <path d="M2 12h2" />
                    <path d="M9 11v2" />
                    <path d="M15 11v2" />
                    <path d="M20 12h2" />
                  </svg>
                  AI Studio
                </div>
                <span class="ms-auto inline-flex items-center gap-2 py-px px-1.5 rounded text-[10px] leading-4 font-medium bg-blue-100 text-blue-800 dark:bg-neutral-700 dark:text-neutral-300">
                  Upcoming
                </span>
              </a>
            </li> -->
            <!-- End Link -->

            <!-- Link -->
            <!-- <li class="px-5">
              <a class="h-9 flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700 items-center gap-1" href="/tools" data-navigo>
                <div class="flex items-center gap-x-3">
                  <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-joystick shrink-0 mt-0.5 size-4">
                  <path
                    d="M21 17a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2Z" />
                  <path d="M6 15v-2" />
                  <path d="M12 15V9" />
                  <circle cx="12" cy="6" r="3" />
                  </svg>
                  Tools
                </div>
                <span class="ms-auto items-center gap-2 py-px px-1.5 rounded text-[10px] leading-4 font-medium bg-blue-100 text-blue-800 dark:bg-neutral-700 dark:text-neutral-300">
                  Upcoming
                </span>
              </a>
            </li> -->
            <!-- End Link -->

            <!-- Link -->
            <!-- <li class="px-5">
              <a
                class="flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700"
                href="/analytics"
                data-navigo>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-chart-no-axes-combined shrink-0 mt-0.5 size-4">
                  <path d="M12 16v5" />
                  <path d="M16 14v7" />
                  <path d="M20 10v11" />
                  <path
                    d="m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15" />
                  <path d="M4 18v3" />
                  <path d="M8 14v7" />
                </svg>
                Analytics
              </a>
            </li> -->
            <!-- End Link -->

            <!-- Divider -->
            <li
              class="pt-5 px-8 mt-5 border-t border-gray-200 first:border-transparent first:pt-0 dark:border-neutral-700 dark:first:border-transparent">
              <span class="block text-xs uppercase text-gray-500 dark:text-neutral-500">
                Workspace
              </span>
            </li>
            <!-- End Divider -->

            <!-- Link -->
            <li class="px-5">
              <a class="flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700"
                href="/domain" data-navigo>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="lucide lucide-globe shrink-0 mt-0.5 size-4">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                  <path d="M2 12h20" />
                </svg>
                Custom Domain
              </a>
            </li>
            <!-- End Link -->

            <!-- Link -->
            <!-- <li class="px-5">
              <a
                class="flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700"
                href="/team-members-listing"
                data-navigo>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-users shrink-0 mt-0.5 size-4">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>

                Team Members
              </a>
            </li> -->
            <!-- End Link -->

            <!-- Link -->
            <li class="px-5">
              <a class="flex gap-x-3 py-2 px-3 text-sm text-gray-800 rounded-lg hover:bg-gray-100 focus:outline-none focus:bg-gray-100 dark:hover:bg-neutral-700 dark:text-neutral-300 dark:focus:bg-neutral-700"
                href="/workspaces/settings" data-navigo>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  class="lucide lucide-bolt shrink-0 mt-0.5 size-4">
                  <path
                    d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
                Settings
              </a>
            </li>
            <!-- End Link -->
          </ul>
        </nav>
        <!-- End Nav -->
        <!-- Sidebar Upgrade Box -->
          ${children.sidebarUpgradeBox.render()}
        <!-- End Sidebar Upgrade Box -->
      </div>
      <!-- End Content -->

      <footer class="border-t border-gray-200 dark:border-neutral-700">
        <!-- Project Dropdown -->
        ${children.workspaceDropdown.render()}
        <!-- End Project Dropdown -->
      </footer>

      <div class="lg:hidden absolute top-3 -end-3 z-10">
        <!-- Sidebar Close -->
        <button type="button"
          class="w-6 h-7 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 focus:outline-none focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
          data-hs-overlay="#hs-pro-sidebar">
          <svg class="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="7 8 3 12 7 16" />
            <line x1="21" x2="11" y1="12" y2="12" />
            <line x1="21" x2="11" y1="6" y2="6" />
            <line x1="21" x2="11" y1="18" y2="18" />
          </svg>
        </button>
        <!-- End Sidebar Close -->
      </div>
    </div>
    </aside>
    <!-- ========== END MAIN SIDEBAR ========== -->
    `;
  }

  return { init, render, updateSidebarActiveItem, children };
}